import { App, ProductIntro } from '@/services/graphQL/api/generated/types_web';

export interface VersionStats {
  version: string;
  completed: boolean;
  skipCount: number;
}

// Get all stored versions from localStorage
export function getStoredVersions(): VersionStats[] {
  try {
    const stored = localStorage.getItem('productIntroVersions');
    if (!stored) return [];

    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    // If parsing fails, return empty array
    return [];
  }
}

export function storeVersions(versions: VersionStats[]): void {
  try {
    localStorage.setItem('productIntroVersions', JSON.stringify(versions));
  } catch (e) {
    console.warn('Failed to store product intro versions:', e);
  }
}

export function getVersionStats(version: string): VersionStats | undefined {
  const versions = getStoredVersions();
  return versions.find((v) => v.version === version);
}

export function updateVersionStats(
  version: string,
  updates: Partial<Omit<VersionStats, 'version'>>
): void {
  const versions = getStoredVersions();
  const existingIndex = versions.findIndex((v) => v.version === version);

  if (existingIndex >= 0) {
    // Update existing version
    versions[existingIndex] = { ...versions[existingIndex], ...updates };
  } else {
    // Add new version
    versions.push({
      version,
      completed: false,
      skipCount: 0,
      ...updates,
    });
  }

  storeVersions(versions);
}

export function isVersionCompleted(version: string): boolean {
  const stats = getVersionStats(version);
  return stats?.completed || false;
}

export function hasVersionReachedMaxSkips(version: string, maxSkips: number): boolean {
  if (maxSkips === 0) {
    return false;
  }
  const stats = getVersionStats(version);
  return (stats?.skipCount || 0) >= maxSkips;
}

// compares the local storage product intro skip count to the max skips defined in app
export function checkMaxSkips(app?: App): boolean {
  if (!app || !app.productIntroVersion) {
    return false;
  }

  const maxSkips = app?.productIntroMaxSkips ?? 0;
  if (maxSkips === 0) {
    return false;
  }
  const currentVersion = app.productIntroVersion;

  return hasVersionReachedMaxSkips(currentVersion, maxSkips);
}

export function checkProductIntroValidity(productIntro?: ProductIntro): boolean {
  if (!productIntro) {
    return false;
  }

  // product intro should have screens, and each screen should have continueButtonCopy
  return (
    productIntro.screens.length > 0 &&
    productIntro.screens.every((screen) => screen.continueButtonCopy)
  );
}

export function compareProductIntroVersions(app?: App): boolean {
  if (!app || !app.productIntroVersion) {
    return false;
  }

  const currentVersion = app.productIntroVersion;

  if (isVersionCompleted(currentVersion)) {
    return false;
  }

  const maxSkips = app.productIntroMaxSkips ?? 0;
  if (hasVersionReachedMaxSkips(currentVersion, maxSkips)) {
    return false;
  }

  return true;
}

export function shouldShowProductIntro(
  app?: App | null,
  productIntro?: ProductIntro | null
): boolean {
  if (!app || !productIntro) {
    return false;
  }

  // if user has reached max skips, do not show product intro
  if (checkMaxSkips(app)) {
    return false;
  }

  return (
    compareProductIntroVersions(app) && // true if api app version is newer than stored version
    checkProductIntroValidity(productIntro) // true if product intro data is valid
  );
}
